import { useCallback, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { addDays, startOfMonth } from "date-fns";
import ConfirmationDialog from "../../shared/components/ConfirmationDialog";
import { formatDateKey, formatDateOnly } from "../../shared/utils/dateUtils";
import { getApiErrorMessage } from "../../shared/utils/apiError";
import type { Sector } from "../types/Sector";
import DepartmentAvailabilityDialog from "../components/DepartmentAvailabilityDialog";
import {
  DepartmentUnavailability,
  DepartmentUnavailabilityInput,
  useDeleteDepartmentUnavailability,
} from "../departmentAvailability";

interface Options {
  zonas: Sector[];
  /** Mes que se está mirando: el alta desde la cabecera arranca ahí (o en hoy, si es el mes en curso). */
  selectedDate: Date;
}

/**
 * Estado y diálogos de "fuera de servicio" del calendario, fuera de Calendar.tsx.
 * Devuelve los handlers y un nodo `elementos` que hay que renderizar una vez.
 * La invalidación de datos la hacen las mutaciones (misma clave para calendario y formularios).
 */
export const useFueraDeServicio = ({ zonas, selectedDate }: Options) => {
  const [form, setForm] = useState<DepartmentUnavailabilityInput | DepartmentUnavailability | null>(null);
  const [aQuitar, setAQuitar] = useState<DepartmentUnavailability | null>(null);
  const [aviso, setAviso] = useState("");
  const quitar = useDeleteDepartmentUnavailability();

  const marcar = useCallback(
    (fecha?: Date, departamento?: string) => {
      const hoy = new Date();
      const esMesEnCurso =
        selectedDate.getFullYear() === hoy.getFullYear() && selectedDate.getMonth() === hoy.getMonth();
      const desde = fecha ?? (esMesEnCurso ? hoy : startOfMonth(selectedDate));
      setForm({
        departamento: departamento ?? "",
        desde: formatDateKey(desde),
        disponible_nuevamente: formatDateKey(addDays(desde, 1)),
        motivo: "",
      });
    },
    [selectedDate]
  );

  const editar = useCallback((block: DepartmentUnavailability) => setForm(block), []);
  const pedirQuitar = useCallback((block: DepartmentUnavailability) => setAQuitar(block), []);

  const confirmarQuitar = async () => {
    if (!aQuitar) return;
    try {
      await quitar.mutateAsync(aQuitar.id);
      setAQuitar(null);
    } catch (error) {
      setAviso(getApiErrorMessage(error, "No se pudo quitar el fuera de servicio"));
    }
  };

  const elementos = (
    <>
      {form && (
        <DepartmentAvailabilityDialog
          open
          zonas={zonas}
          initial={form}
          onClose={() => setForm(null)}
          onSaved={() => setForm(null)}
        />
      )}

      <ConfirmationDialog
        open={Boolean(aQuitar)}
        onClose={() => (quitar.isPending ? undefined : setAQuitar(null))}
        onConfirm={confirmarQuitar}
        loading={quitar.isPending}
        loadingText="Quitando..."
        title="Quitar fuera de servicio"
        message={
          aQuitar
            ? `El Depto ${aQuitar.departamento} vuelve a estar disponible desde el ${formatDateOnly(aQuitar.desde)}.`
            : ""
        }
        confirmText="Quitar"
        type="delete"
        severity="warning"
      />

      <Snackbar open={Boolean(aviso)} autoHideDuration={5000} onClose={() => setAviso("")}>
        <Alert severity="error" onClose={() => setAviso("")}>
          {aviso}
        </Alert>
      </Snackbar>
    </>
  );

  return { marcar, editar, pedirQuitar, elementos };
};
